import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit, Trash2, Copy } from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';

const empty = { code: '', description: '', type: 'percent', value: 10, min_subtotal: 0, max_uses: 0, valid_from: '', valid_until: '', enabled: true };

const AdminCoupons = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.listCoupons().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c.id); setForm({ ...empty, ...c, valid_from: c.valid_from || '', valid_until: c.valid_until || '' }); setOpen(true); };
  const save = async () => {
    if (!form.code) return toast.error('Code required');
    const payload = { ...form };
    if (!payload.valid_from) delete payload.valid_from;
    if (!payload.valid_until) delete payload.valid_until;
    try {
      if (editing) await api.updateCoupon(editing, payload);
      else await api.createCoupon(payload);
      toast.success(editing ? 'Updated' : 'Created');
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => { if (!window.confirm('Delete this coupon?')) return; try { await api.deleteCoupon(id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } };
  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success(`Copied ${code}`); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-zinc-400">Promo codes — percentage off, fixed amount, or free shipping. Customers paste them at checkout.</p>
        <Button onClick={openNew} className="btn-gold"><Plus className="h-4 w-4 mr-2" /> New code</Button>
      </div>

      <Card className="bg-zinc-950 border-zinc-900 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60"><tr className="text-left text-zinc-400"><th className="p-3">Code</th><th className="p-3">Type</th><th className="p-3">Value</th><th className="p-3">Min subtotal</th><th className="p-3">Used</th><th className="p-3">Valid until</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-zinc-500">No promo codes yet</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-zinc-900 hover:bg-zinc-900/30">
                <td className="p-3"><div className="flex items-center gap-2"><span className="font-mono text-[#d4af37] tracking-wider">{c.code}</span><button onClick={() => copyCode(c.code)} className="text-zinc-500 hover:text-[#d4af37]"><Copy className="h-3.5 w-3.5" /></button></div><p className="text-zinc-500 text-xs">{c.description}</p></td>
                <td className="p-3 text-zinc-300">{c.type}</td>
                <td className="p-3 text-zinc-100">{c.type === 'percent' ? `${c.value}%` : c.type === 'fixed' ? `€${c.value}` : '—'}</td>
                <td className="p-3 text-zinc-300">€{c.min_subtotal}</td>
                <td className="p-3 text-zinc-300">{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ''}</td>
                <td className="p-3 text-zinc-300">{c.valid_until ? c.valid_until.slice(0, 10) : '—'}</td>
                <td className="p-3"><Badge variant="outline" className={c.enabled ? 'border-emerald-600 text-emerald-400' : 'border-zinc-700 text-zinc-400'}>{c.enabled ? 'Active' : 'Disabled'}</Badge></td>
                <td className="p-3 text-right"><Button size="sm" variant="ghost" className="text-zinc-300 hover:text-[#d4af37]" onClick={() => openEdit(c)}><Edit className="h-4 w-4" /></Button><Button size="sm" variant="ghost" className="text-zinc-300 hover:text-rose-500" onClick={() => del(c.id)}><Trash2 className="h-4 w-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-900 text-zinc-100 max-w-xl">
          <DialogHeader><DialogTitle className="serif-italic text-[#d4af37] text-2xl">{editing ? 'Edit promo code' : 'New promo code'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Code (uppercase, no spaces)</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s+/g, '') })} className="bg-transparent border-zinc-800 mt-1 font-mono uppercase" placeholder="WELCOME10" /></div>
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="bg-transparent border-zinc-800 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage off</SelectItem>
                    <SelectItem value="fixed">Fixed amount off</SelectItem>
                    <SelectItem value="free_shipping">Free shipping</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.type !== 'free_shipping' && <div><Label>{form.type === 'percent' ? 'Percent (0–100)' : 'Amount (€)'}</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) || 0 })} className="bg-transparent border-zinc-800 mt-1" /></div>}
              <div><Label>Minimum subtotal (€)</Label><Input type="number" value={form.min_subtotal} onChange={(e) => setForm({ ...form, min_subtotal: parseFloat(e.target.value) || 0 })} className="bg-transparent border-zinc-800 mt-1" /></div>
              <div><Label>Max uses (0 = unlimited)</Label><Input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: parseInt(e.target.value) || 0 })} className="bg-transparent border-zinc-800 mt-1" /></div>
              <div><Label>Valid from</Label><Input type="date" value={(form.valid_from || '').slice(0, 10)} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /></div>
              <div><Label>Valid until</Label><Input type="date" value={(form.valid_until || '').slice(0, 10)} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /></div>
            </div>
            <div><Label>Description (shown to admin)</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-transparent border-zinc-800 mt-1" placeholder="e.g. 10% off for first-time buyers" /></div>
            <div className="flex items-center justify-between border border-zinc-900 rounded p-3"><span className="text-zinc-300 text-sm">Active</span><Switch checked={!!form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} /></div>
          </div>
          <div className="mt-6 flex justify-end gap-3"><Button variant="outline" className="btn-outline-gold" onClick={() => setOpen(false)}>Cancel</Button><Button className="btn-gold" onClick={save}>{editing ? 'Update' : 'Create'}</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCoupons;
